import {ChangeDetectorRef, Component, inject, OnInit, OnDestroy, HostListener, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CanComponentDeactivate } from '../guards/unsaved-changes.guard';
import { UnsavedChangesTrackerService } from '../services/unsaved-changes/unsaved-changes-tracker.service';
import { UnsavedChangesHostHelper } from '../services/unsaved-changes/unsaved-changes-host.helper';
import {
  ColDef,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  PaginationChangedEvent,
} from 'ag-grid-community';
import { AgGridAngular } from 'ag-grid-angular';
import { AgGridModule } from 'ag-grid-angular';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { CellClickedEvent } from 'ag-grid-community';
import { Router, RouterLink } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import moment from 'moment';
import { FormsModule } from '@angular/forms';
import { get } from 'lodash'; // for copy data cell value
import {
  AG_GRID_DEFAULT_PAGE_SIZE,
  AG_GRID_PAGE_SIZE_OPTIONS,
  AG_GRID_DEFAULT_ROW_HEIGHT_PX,
  AG_GRID_EMPTY_BODY_ROW_PX,
  AG_GRID_HEADER_AND_PAGING_PX,
  syncInfiniteRowModelPageSize,
} from '../shared/ag-grid/ag-grid-pagination.util';
import {
  CUSTOMER_TYPE_ID_CUSTOMER,
  CUSTOMER_TYPE_ID_RESELLER,
  CustomerService,
} from '../services/customer-service/customer.service';
import { MatDialog } from '@angular/material/dialog';
import { EditCustomerListComponent, CustomerDialogData } from '../Dialogs/edit-customer-list/edit-customer-list.component';
import { ButtonCellRendererCustomerListComponent } from '../shared/components/button-cell-renderer/button-cell-renderer-customer-list.component';
import { ConfirmDeleteCustomerComponent } from '../user-management/confirm-delete-customer/confirm-delete-customer.component';
import { ToastrService } from 'ngx-toastr';
import { createAgGridSelectFloatingFilter } from '../shared/ag-grid/select-floating-filter';
import { HasPermissionDirective } from '../directives/has-permission.directive';
import { RbacPermissions } from '../services/rbac/rbac-permissions';
import { RbacService } from '../services/rbac/rbac.service';
import { AgGridCopyCellTooltipDirective } from '../shared/ag-grid/ag-grid-copy-cell-tooltip.directive';
/** API sort for newest customers first (recent add/update at top). */
const NEWEST_FIRST_SORT = { field: 'createdAt', direction: 'desc' as const };
const NEWEST_FIRST_GRID_COL = { colId: 'createdAt', sort: 'desc' as const };
const SESSION_SORT_KEY = 'customerListSortingState';
const CLIENT_FILTER_FETCH_SIZE = 1000;

interface CustomerListFilter {
  field: string;
  operator: string;
  value: string;
}

/** Floating filter dropdown for Customer Type (mirrors Group Type on Group Management). */
const CustomerTypeFloatingFilter = createAgGridSelectFloatingFilter([
  { label: 'All', value: '' },
  { label: 'Reseller', value: 'Reseller' },
  { label: 'Customer', value: 'Customer' },
]);

/** Cloud Provider values match Add/Edit Customer dialog options. */
const CloudProviderFloatingFilter = createAgGridSelectFloatingFilter([
  { label: 'All', value: '' },
  { label: 'AWS', value: 'AWS' },
  { label: 'GCP', value: 'GCP' },
  { label: 'WMT-GCP', value: 'WMT-GCP' },
]);

/** Status enum floating filter (Active / Inactive), same pattern as Device/User List. */
const CustomerStatusFloatingFilter = createAgGridSelectFloatingFilter([
  { label: 'All', value: '' },
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
]);

@Component({
  selector: 'app-customer-list',
  standalone: true,
  imports: [
    AgGridAngular,
    MatIconModule,
    MatButtonModule,
    AgGridModule,
    CommonModule,
    MatInputModule,
    MatSelectModule,
    RouterLink,
    FormsModule,
    HasPermissionDirective,
    AgGridCopyCellTooltipDirective,
  ],
  templateUrl: './customer-list.component.html',
  styleUrl: './customer-list.component.scss'
})
export class CustomerListComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  readonly RbacPermissions = RbacPermissions;

  private readonly rbacService = inject(RbacService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly unsavedHost = new UnsavedChangesHostHelper(
    inject(UnsavedChangesTrackerService),
    inject(MatDialog),
    inject(Router)
  );
  private readonly cdr = inject(ChangeDetectorRef);
  private fullColumnDefs: ColDef[] | null = null;

  constructor(
    private customerService: CustomerService,
    private dialog: MatDialog,
    private toastr: ToastrService
  ) {}

  canDeactivate(): Promise<boolean> {
    return this.unsavedHost.canDeactivate();
  }

  @HostListener('window:beforeunload', ['$event'])
  onWindowBeforeUnload(event: BeforeUnloadEvent): void {
    this.unsavedHost.onBeforeUnload(event);
  }

  ngOnDestroy(): void {
    this.unsavedHost.clearOnDestroy();
  }

    // columnDefs End for ag grid
  rowData: any[] = [];
  paginationPageSize = AG_GRID_DEFAULT_PAGE_SIZE;
  readonly pageSizeOptions = AG_GRID_PAGE_SIZE_OPTIONS;
  /** Host height from loaded rows — page scrolls, not the grid. */
  gridHostHeightPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
  gridApi: any;
  gridColumnApi: any;
  sortFlag: boolean = false;
  currentPage:any;

  // Add these properties for filtering
  customerNameFilter: string = '';
  accountNumberFilter: string = '';
  customerList: any[] = [];
  selectedCustomer: string = 'All';
  customerTypeFilterValue = '';
  cloudProviderFilterValue = '';
  statusFilterValue = '';
  private loadErrorShown = false;
  private clientFilteredRows: any[] | null = null;
  private syncingHeaderColumnFilters = false;

  ngOnInit(): void {
    this.applyRbacToGrid();
    this.rbacService.contextChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.applyRbacToGrid());
    this.rbacService.ensureContextLoaded().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    // Load customer list for dropdown
    this.getAllCustomersForDropdown();
    const storedCustomerListSelectedCustomer = sessionStorage.getItem('customerListSelectedCustomer');
    if(storedCustomerListSelectedCustomer)
    {
      this.selectedCustomer = storedCustomerListSelectedCustomer;
      this.applyFilters();

    }
    const storedCustomerListAccountNumberFilter = sessionStorage.getItem('customerListAccountNumberFilter');
    if(storedCustomerListAccountNumberFilter)
    {
      this.accountNumberFilter = storedCustomerListAccountNumberFilter;
      this.applyFilters();

    }
    //sorting
    const storedSorting = sessionStorage.getItem(SESSION_SORT_KEY);
    if (storedSorting) {
      const customerListSortingState = JSON.parse(storedSorting);
      const sortField = this.resolveApiSortField(customerListSortingState.colId);
      this.payloadOfgetCustomerList.sorts[0].field = sortField;
      this.payloadOfgetCustomerList.sorts[0].direction = customerListSortingState.sort;
    }
  }

  onPaginationChanged(event: PaginationChangedEvent): void {
    this.currentPage = event.api.paginationGetCurrentPage();

    const pageSizeChanged = syncInfiniteRowModelPageSize(
      event,
      this.paginationPageSize,
      (newPageSize) => {
        this.paginationPageSize = newPageSize;
        this.payloadOfgetCustomerList.size = newPageSize;
        this.payloadOfgetCustomerList.page = 0;
        this.clientFilteredRows = null;
        this.clientFilteredCacheKey = '';
        this.reloadCustomerGrid();
      }
    );

    if (pageSizeChanged) {
      this.currentPage = 0;
    }
  }

  onDeleteCustomer(row: Record<string, unknown>): void {
    const deviceCount = Number(row['deviceCount']);
    if (Number.isFinite(deviceCount) && deviceCount > 0) {
      this.toastr.warning(
        '<b>Warning</b>: All devices must be removed or moved before deleting this customer.',
        '',
        { timeOut: 8000, closeButton: true, progressBar: true, enableHtml: true }
      );
      return;
    }

    const recordId = this.resolveCustomerIdFromRow(row);
    if (recordId == null) {
      this.toastr.error('Missing customer id ΓÇö cannot delete this customer.', 'Delete Failed', {
        timeOut: 5000,
        closeButton: true,
        progressBar: true,
      });
      return;
    }

    const customerName = String(row['customerName'] ?? 'N/A');
    const accountNumber = row['accountNumber']
      ? String(row['accountNumber'])
      : undefined;
    // Dialog shows business customerId; delete API still uses internal record id.
    const displayCustomerId =
      row['customerId'] != null && String(row['customerId']).trim() !== ''
        ? row['customerId']
        : 'N/A';

    const dialogRef = this.dialog.open(ConfirmDeleteCustomerComponent, {
      width: '520px',
      restoreFocus: false,
      data: {
        customerId: displayCustomerId,
        customerName,
        accountNumber,
      },
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) {
        return;
      }

      this.customerService.deleteCustomer(recordId).subscribe({
        next: () => {
          this.toastr.success(
            `Customer "${customerName}" has been deleted successfully.`,
            'Customer Deleted',
            { timeOut: 4000, closeButton: true, progressBar: true }
          );
          this.refreshGridShowNewestFirst();
          this.getAllCustomersForDropdown();
        },
        error: (err) => {
          const msg =
            err?.error?.detail ??
            err?.error?.message ??
            err?.message ??
            `Failed to delete customer "${customerName}". Please try again.`;
          this.toastr.error(msg, 'Delete Failed', {
            timeOut: 6000,
            closeButton: true,
            progressBar: true,
          });
        },
      });
    });
  }

  onAddCustomer(): void {
    const dialogData: CustomerDialogData = { mode: 'add' };
    const dialogRef = this.dialog.open(EditCustomerListComponent, {
      width: '80vw',
      maxWidth: '1000px',
      panelClass: 'customer-dialog-panel',
      restoreFocus: false,
      closeOnNavigation: false,
      data: dialogData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.resetCustomerNameFilterAfterMutation();
        this.refreshGridShowNewestFirst();
        this.getAllCustomersForDropdown();
      }
    });
  }

  payloadOfgetCustomerList = {
    page: 0,
    size: AG_GRID_DEFAULT_PAGE_SIZE,
    filters: [
      {
        field: '',
        operator: '',
        value: '',
      },
    ],
    sorts: [{ ...NEWEST_FIRST_SORT }],
  };

  dataSource: IDatasource = {
    getRows: (params: IGetRowsParams) => {
      this.sortForAgGrid(params.sortModel);
      const filterModel = this.resolveActiveFilterModel(params.filterModel);
      const customerTypeSelection = this.resolveCustomerTypeFilterSelection(filterModel);
      const needsClientFilter = customerTypeSelection != null;

      this.filterForAgGrid(filterModel, {
        skipCustomerType: needsClientFilter,
      });

      if (needsClientFilter) {
        this.loadClientFilteredRows(params, customerTypeSelection);
        return;
      }

      this.clientFilteredRows = null;
      this.payloadOfgetCustomerList.page = Math.floor(
        params.startRow / this.paginationPageSize
      );
      this.payloadOfgetCustomerList.size = this.paginationPageSize;
      this.fetchCustomerRows(params);
    },
  };

  onCustomerTypeFilterChange(value: string): void {
    this.customerTypeFilterValue = value ?? '';
  }

  onCloudProviderFilterChange(value: string): void {
    this.cloudProviderFilterValue = value ?? '';
  }

  private resolveActiveFilterModel(
    paramsFilterModel?: Record<string, unknown> | null
  ): Record<string, unknown> {
    return {
      ...(paramsFilterModel ?? {}),
      ...(this.gridApi?.getFilterModel?.() ?? {}),
    };
  }

  private fetchCustomerRows(params: IGetRowsParams): void {
    this.customerService.getAllCustomer(this.payloadOfgetCustomerList).subscribe({
      next: (data) => {
        this.loadErrorShown = false;
        const content = data?.data?.content ?? [];
        this.rowData = content;
        this.total_number_of_records = data?.data?.totalElements ?? 0;
        this.syncGridHostHeight(content.length);
        params.successCallback(content, this.total_number_of_records);
      },
      error: (err) => this.handleCustomerRowsError(params, err),
    });
  }

  private loadClientFilteredRows(
    params: IGetRowsParams,
    customerTypeSelection: string | null
  ): void {
    const cacheKey = JSON.stringify({
      filters: this.payloadOfgetCustomerList.filters,
      sorts: this.payloadOfgetCustomerList.sorts,
      customerTypeSelection,
    });

    if (this.clientFilteredRows && this.clientFilteredCacheKey === cacheKey) {
      this.deliverClientFilteredPage(params);
      return;
    }

    const fetchPayload = {
      ...this.payloadOfgetCustomerList,
      page: 0,
      size: CLIENT_FILTER_FETCH_SIZE,
    };

    this.customerService.getAllCustomer(fetchPayload).subscribe({
      next: (data) => {
        this.loadErrorShown = false;
        let rows = data?.data?.content ?? [];
        rows = this.applyCustomerTypeClientFilter(rows, customerTypeSelection);
        this.clientFilteredRows = rows;
        this.clientFilteredCacheKey = cacheKey;
        this.total_number_of_records = rows.length;
        this.deliverClientFilteredPage(params);
      },
      error: (err) => this.handleCustomerRowsError(params, err),
    });
  }

  private clientFilteredCacheKey = '';

  private deliverClientFilteredPage(params: IGetRowsParams): void {
    const rows = this.clientFilteredRows ?? [];
    const pageRows = rows.slice(
      params.startRow,
      params.startRow + this.paginationPageSize
    );
    this.rowData = pageRows;
    this.syncGridHostHeight(pageRows.length);
    params.successCallback(pageRows, rows.length);
  }

  private handleCustomerRowsError(params: IGetRowsParams, err: unknown): void {
    if (!this.loadErrorShown) {
      this.loadErrorShown = true;
      const error = err as {
        error?: { detail?: string; message?: string };
        message?: string;
      };
      const msg =
        error?.error?.detail ??
        error?.error?.message ??
        error?.message ??
        'Failed to load customers. Please try again.';
      this.toastr.error(msg, 'Load Failed', {
        timeOut: 8000,
        closeButton: true,
        progressBar: true,
      });
    }
    this.syncGridHostHeight(0);
    params.failCallback();
  }

  /**
   * Size host to the loaded page of rows (default fixed row height).
   * Full content height so the grid does not need an internal vertical scrollbar.
   */
  private syncGridHostHeight(loadedRowCount: number): void {
    const rows = Number.isFinite(loadedRowCount) && loadedRowCount > 0 ? loadedRowCount : 0;
    const bodyPx =
      rows > 0 ? rows * AG_GRID_DEFAULT_ROW_HEIGHT_PX : AG_GRID_EMPTY_BODY_ROW_PX;
    const contentPx = AG_GRID_HEADER_AND_PAGING_PX + bodyPx;
    const minHostPx = AG_GRID_HEADER_AND_PAGING_PX + AG_GRID_EMPTY_BODY_ROW_PX;
    this.gridHostHeightPx = Math.max(minHostPx, contentPx);
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    const storedSorting = sessionStorage.getItem(SESSION_SORT_KEY);
    if (storedSorting) {
      const customerListSortingState = JSON.parse(storedSorting);
      const colId = this.resolveGridSortColId(customerListSortingState.colId);
      if (colId) {
        this.gridApi.applyColumnState({
          state: [{ colId, sort: customerListSortingState.sort }],
          applyOrder: false,
        });
      }
    } else {
      this.applyNewestFirstSortToPayload();
    }

    setTimeout(() => {
      this.payloadOfgetCustomerList.page = 0;
      this.restoreHeaderFiltersToGrid();
      this.gridApi?.purgeInfiniteCache();
      this.gridApi?.setGridOption('datasource', this.dataSource);
    }, 0);
  }

  onFilterChanged(): void {
    if (this.syncingHeaderColumnFilters) {
      return;
    }
    this.syncHeaderFiltersFromGrid();
    this.clientFilteredRows = null;
    this.clientFilteredCacheKey = '';
    this.refreshCustomerGrid();
  }

  onStatusFilterChange(value: string): void {
    this.statusFilterValue = value ?? '';
    // Fallback when parent filter model update is delayed (AG Grid 33).
    this.clientFilteredRows = null;
    this.clientFilteredCacheKey = '';
    this.refreshCustomerGrid();
  }

  /** Header Customer dropdown → Organization Name column filter. */
  onCustomerDropdownChange(event: any) {
    this.selectedCustomer = event.target.value;
    sessionStorage.setItem('customerListSelectedCustomer', this.selectedCustomer);
    this.applyHeaderFiltersToGrid({
      customerName: this.selectedCustomer === 'All' ? null : this.selectedCustomer,
    });
  }

  /** Header Account # input → Account # column filter. */
  onAccountNumberChange(value: string) {
    this.accountNumberFilter = value ?? '';
    sessionStorage.setItem(
      'customerListAccountNumberFilter',
      this.accountNumberFilter
    );
    this.applyHeaderFiltersToGrid({
      accountNumber: this.accountNumberFilter.trim() || null,
    });
  }

  private applyHeaderFiltersToGrid(updates: {
    customerName?: string | null;
    accountNumber?: string | null;
  }): void {
    if (!this.gridApi) {
      this.applyFilters();
      return;
    }
    const textContains = (value: string) => ({
      filterType: 'text',
      type: 'contains',
      filter: value,
    });

    this.syncingHeaderColumnFilters = true;

    const applyColumnTextFilter = async (
      colId: string,
      value: string | null
    ): Promise<void> => {
      const model = value ? textContains(value) : null;

      if (typeof this.gridApi.getColumnFilterInstance === 'function') {
        const instance = (await Promise.resolve(
          this.gridApi.getColumnFilterInstance(colId)
        )) as { setModel?: (m: unknown) => void } | null;
        if (instance?.setModel) {
          instance.setModel(model);
          return;
        }
      }

      if (typeof this.gridApi.setColumnFilterModel === 'function') {
        await Promise.resolve(this.gridApi.setColumnFilterModel(colId, model));
        return;
      }

      const nextModel: Record<string, unknown> = {
        ...(this.gridApi.getFilterModel() || {}),
      };
      if (model) {
        nextModel[colId] = model;
      } else {
        delete nextModel[colId];
      }
      this.gridApi.setFilterModel(nextModel);
    };

    const tasks: Promise<void>[] = [];
    if (updates.customerName !== undefined) {
      tasks.push(applyColumnTextFilter('customerName', updates.customerName));
    }
    if (updates.accountNumber !== undefined) {
      tasks.push(applyColumnTextFilter('accountNumber', updates.accountNumber));
    }

    Promise.all(tasks)
      .then(() => {
        this.gridApi?.onFilterChanged?.();
        this.applyFilters();
      })
      .finally(() => {
        queueMicrotask(() => {
          this.syncingHeaderColumnFilters = false;
        });
      });
  }

  private restoreHeaderFiltersToGrid(): void {
    this.applyHeaderFiltersToGrid({
      customerName:
        this.selectedCustomer && this.selectedCustomer !== 'All'
          ? this.selectedCustomer
          : null,
      accountNumber: this.accountNumberFilter?.trim() || null,
    });
  }

  private syncHeaderFiltersFromGrid(): void {
    if (!this.gridApi) {
      return;
    }
    const model = this.gridApi.getFilterModel() || {};
    let headerChanged = false;

    const customerFilter = String(model['customerName']?.filter ?? '').trim();
    if (!customerFilter) {
      if (this.selectedCustomer !== 'All') {
        this.selectedCustomer = 'All';
        sessionStorage.setItem('customerListSelectedCustomer', 'All');
        headerChanged = true;
      }
    } else {
      const exact = this.customerList.find((c: any) => c.customerName === customerFilter);
      if (exact && this.selectedCustomer !== exact.customerName) {
        this.selectedCustomer = exact.customerName;
        sessionStorage.setItem('customerListSelectedCustomer', this.selectedCustomer);
        headerChanged = true;
      }
    }

    const accountFilter = String(model['accountNumber']?.filter ?? '').trim();
    if (this.accountNumberFilter !== accountFilter) {
      this.accountNumberFilter = accountFilter;
      if (accountFilter) {
        sessionStorage.setItem('customerListAccountNumberFilter', accountFilter);
      } else {
        sessionStorage.removeItem('customerListAccountNumberFilter');
      }
      headerChanged = true;
    }

    const statusFilter = String(model['status']?.filter ?? '').trim();
    if (this.statusFilterValue !== statusFilter) {
      this.statusFilterValue = statusFilter;
      headerChanged = true;
    }

    if (headerChanged) {
      this.cdr.markForCheck();
    }
  }

  private refreshCustomerGrid(): void {
    if (!this.gridApi) {
      return;
    }
    this.clientFilteredRows = null;
    this.clientFilteredCacheKey = '';
    this.payloadOfgetCustomerList.page = 0;
    this.gridApi.paginationGoToFirstPage();
    this.gridApi.purgeInfiniteCache();
  }

  /** Reset filters so a newly added/updated customer is visible in the grid. */
  private resetCustomerNameFilterAfterMutation(): void {
    this.selectedCustomer = 'All';
    this.accountNumberFilter = '';
    this.customerTypeFilterValue = '';
    this.cloudProviderFilterValue = '';
    this.statusFilterValue = '';
    this.clientFilteredRows = null;
    this.clientFilteredCacheKey = '';
    sessionStorage.setItem('customerListSelectedCustomer', 'All');
    sessionStorage.removeItem('customerListAccountNumberFilter');
    this.gridApi?.setFilterModel(null);
    this.refreshCustomerGrid();
  }

  private reloadCustomerGrid(): void {
    if (!this.gridApi) {
      return;
    }
    this.gridApi.purgeInfiniteCache();
  }

  /** After add/update: sort newest first, go to page 1, reload data. */
  private refreshGridShowNewestFirst(): void {
    if (!this.gridApi) return;

    sessionStorage.setItem(
      SESSION_SORT_KEY,
      JSON.stringify(NEWEST_FIRST_GRID_COL)
    );
    this.applyNewestFirstSortToPayload();
    this.gridApi.paginationGoToFirstPage();
    this.reloadCustomerGrid();
  }

  private applyNewestFirstSortToPayload(): void {
    this.payloadOfgetCustomerList.sorts[0] = { ...NEWEST_FIRST_SORT };
    this.payloadOfgetCustomerList.page = 0;
  }

  total_number_of_records = 0;
  public router=inject(Router)
  defaultColDef: ColDef = {
    sortable: true,
    sortingOrder: ['asc', 'desc'],
    resizable: true,
    filter: true,
    floatingFilter: true,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
  };

  private readonly textFilterParams = {
    filterOptions: ['contains'],
    defaultOption: 'contains',
    trimInput: true,
    maxNumConditions: 1,
    suppressFilterButton: true,
  };

  // columnDefs Start for ag grid
  columnDefs: ColDef[] = [
    {
      headerName: 'Organization Name',
      field: 'customerName',
      colId: 'customerName',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) => {
        const cName = params?.data?.customerName;
        const cId = params?.data?.customerId;
        if (cName) {
          return `${cName} (${cId})`;
        }
        return 'N/A';
      },
    },
    {
      headerName: 'Account #',
      field: 'accountNumber',
      colId: 'accountNumber',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      cellRenderer: (params: any) =>
        params?.data?.accountNumber ? params?.data?.accountNumber : 'N/A',
    },
    {
      headerName: 'Organization Type',
      field: 'customerTypeName',
      colId: 'customerTypeName',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
        suppressFilterButton: true,
      },
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      floatingFilterComponent: CustomerTypeFloatingFilter,
      floatingFilterComponentParams: {
        onSelectionChange: (value: string) =>
          this.onCustomerTypeFilterChange(value),
      },
      filterValueGetter: (params: any) => this.formatCustomerType(params?.data),
      cellRenderer: (params: any) => this.formatCustomerType(params?.data),
    },
    {
      headerName: 'Device Count',
      field: 'deviceCount',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: this.textFilterParams,
      suppressFloatingFilterButton: true,
      filterValueGetter: (params: any) =>
        params?.data?.deviceCount != null ? String(params.data.deviceCount) : '',
      cellRenderer: (params: any) =>
        params?.data?.deviceCount?.toString()
          ? params?.data?.deviceCount?.toString()
          : 'N/A',
    },
    {
      headerName: 'Cloud Provider',
      field: 'cloudProvider',
      colId: 'cloudProvider',
      flex: 1,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
        suppressFilterButton: true,
      },
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      floatingFilterComponent: CloudProviderFloatingFilter,
      floatingFilterComponentParams: {
        onSelectionChange: (value: string) =>
          this.onCloudProviderFilterChange(value),
      },
      cellRenderer: (params: any) =>
        params?.data?.cloudProvider?.toString()
          ? params?.data?.cloudProvider?.toString()
          : 'N/A',
    },
    {
      headerName: 'Status',
      field: 'status',
      colId: 'status',
      flex: 1,
      filter: 'agTextColumnFilter',
      floatingFilter: true,
      filterParams: {
        filterOptions: ['contains'],
        defaultOption: 'contains',
        maxNumConditions: 1,
        suppressFilterButton: true,
      },
      suppressHeaderMenuButton: true,
      suppressFloatingFilterButton: true,
      filterValueGetter: (params: any) =>
        this.formatCustomerStatus(params?.data),
      floatingFilterComponent: CustomerStatusFloatingFilter,
      floatingFilterComponentParams: {
        onSelectionChange: (value: string) => this.onStatusFilterChange(value),
      },
      cellRenderer: (params: any) =>
        this.formatCustomerStatus(params?.data),
    },
    {
      headerName: 'Action',
      field: 'action',
      flex: 1,
      width: 100,
      sortable: false,
      filter: false,
      floatingFilter: false,
      cellRenderer: ButtonCellRendererCustomerListComponent,
      cellRendererParams: (params: any) => ({
        rowData: params.data,
        onCustomerSaved: () => {
          this.resetCustomerNameFilterAfterMutation();
          this.refreshGridShowNewestFirst();
          this.getAllCustomersForDropdown();
        },
        onDelete: (row: Record<string, unknown>) => this.onDeleteCustomer(row),
      }),
      cellStyle: { textAlign: 'left' },
    },
  ];

  private applyRbacToGrid(): void {
    if (!this.fullColumnDefs) {
      this.fullColumnDefs = [...this.columnDefs];
    }

    const canEdit = this.rbacService.hasPermission(
      RbacPermissions.UserMgmt_Customer_Edit
    );
    const canDelete = this.rbacService.hasPermission(
      RbacPermissions.UserMgmt_Customer_Delete
    );

    this.columnDefs = this.fullColumnDefs.filter((col) => {
      if (col.field === 'action') {
        return canEdit || canDelete;
      }
      return true;
    });

    if (this.gridApi) {
      this.gridApi.setGridOption('columnDefs', this.columnDefs);
    }
  }

  /* Start Copy Data cell Value function   */
  contextMenu: { visible: boolean; x: number; y: number } = {
    visible: false,
    x: 0,
    y: 0
  };
  selectedCellValue: string = '';
  contextMenuVisible: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  getNestedProperty(obj: any, path: string): any {
    return get(obj, path, 'N/A'); // 'N/A' if value is undefined
  }

  onCellContextMenu(event: any) {
    event.event.preventDefault();
    const columnField = event.colDef.field;
    let cellValue = 'N/A';
    if (event.colDef.cellRenderer) {
      const cellRenderer = event.colDef.cellRenderer;
      const rendererParams = { data: event.data, colDef: event.colDef, value: event.value };
      const renderedValue = cellRenderer(rendererParams);
      if (typeof renderedValue === 'string') {
        cellValue = renderedValue.replace(/<[^>]*>/g, '');
      } else if (renderedValue?.outerText) {
        cellValue = renderedValue.outerText;
      }
    } else {
      cellValue = this.getNestedProperty(event.data, columnField);
    }

    // Check if the data and field are available
    if (event.node && event.node.data) {
      this.selectedCellValue = cellValue;
      this.contextMenuPosition = {
        x: event.event.clientX,
        y: event.event.clientY
      };
      this.contextMenuVisible = true;
    } else {
      this.contextMenuVisible = false;
    }
  }

  handleCopy() {
    navigator.clipboard.writeText(this.selectedCellValue)
    .then(() => {
      this.contextMenuVisible = false;
    })
    .catch(err => console.error('Failed to copy:', err));
  }

  // Hide context menu on outside click
  @HostListener('document:click')
  onDocumentClick(): void {
    this.contextMenuVisible = false;
  }
  preventDefaultContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }
  /* End Copy Data cell Value function   */

  sortForAgGrid(sortModel: any) {
    if (
      sortModel[0] == undefined ||
      sortModel[0] == null ||
      sortModel[0].colId == undefined ||
      sortModel[0].colId == null
    ) {
      this.applyNewestFirstSortToPayload();
      return;
    }
    sessionStorage.setItem(SESSION_SORT_KEY, JSON.stringify(sortModel[0]));
    this.sortFlag = true;
    switch (sortModel[0].colId) {
      case 'customerName': {
        this.payloadOfgetCustomerList.sorts[0].field = 'customerName';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'accountNumber': {
        this.payloadOfgetCustomerList.sorts[0].field = 'accountNumber';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'customerId':
      case 'createdAt': {
        this.payloadOfgetCustomerList.sorts[0].field = 'createdAt';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'customerTypeName':
      case 'customerType':
      case 'customerTypeId': {
        // Organization Type display is derived; API sort/filter field is customerTypeId.
        this.payloadOfgetCustomerList.sorts[0].field = 'customerTypeId';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'id': {
        this.payloadOfgetCustomerList.sorts[0].field = 'id';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'latestConfig': {
        this.payloadOfgetCustomerList.sorts[0].field = 'latestConfig';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'deviceCount': {
        this.payloadOfgetCustomerList.sorts[0].field = 'deviceCount';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'cloudProvider': {
        this.payloadOfgetCustomerList.sorts[0].field = 'cloudProvider';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'upToDate': {
        this.payloadOfgetCustomerList.sorts[0].field = 'upToDate';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      case 'status':
      case 'isActive': {
        this.payloadOfgetCustomerList.sorts[0].field = 'status';
        this.payloadOfgetCustomerList.sorts[0].direction = sortModel[0].sort;
        break;
      }
      default: {
      }
    }
  }

  private filterForAgGrid(
    filterModel: Record<string, unknown> | null | undefined,
    options: { skipCustomerType?: boolean } = {}
  ): void {
    const newFilters: CustomerListFilter[] = [];

    if (filterModel && Object.keys(filterModel).length > 0) {
      for (const field of Object.keys(filterModel)) {
        if (field === 'status' || field === 'customerTypeName' || field === 'cloudProvider') {
          continue;
        }
        const mapped = this.mapAgGridFilterToApiFilter(
          field,
          filterModel[field] as Record<string, unknown>
        );
        if (mapped) {
          newFilters.push(mapped);
        }
      }
    }

    // Prefer column filter for customerName; header dropdown mirrors into the column.
    const hasCustomerNameFromGrid = newFilters.some((f) => f.field === 'customerName');
    if (!hasCustomerNameFromGrid && this.selectedCustomer && this.selectedCustomer !== 'All') {
      newFilters.push({
        field: 'customerName',
        operator: 'like',
        value: this.selectedCustomer,
      });
    }

    // Prefer column filter for accountNumber; header input mirrors into the column.
    const hasAccountFromGrid = newFilters.some((f) => f.field === 'accountNumber');
    if (!hasAccountFromGrid && this.accountNumberFilter?.trim()) {
      newFilters.push({
        field: 'accountNumber',
        operator: 'like',
        value: this.accountNumberFilter.trim(),
      });
    }

    if (!options.skipCustomerType) {
      const customerTypeFilter = this.mapCustomerTypeSelectionToApi(
        this.resolveCustomerTypeFilterSelection(filterModel) ?? ''
      );
      if (customerTypeFilter) {
        newFilters.push(customerTypeFilter);
      }
    }

    const cloudProviderFilter = this.mapCloudProviderSelectionToApi(
      this.cloudProviderFilterValue
    );
    if (cloudProviderFilter) {
      newFilters.push(cloudProviderFilter);
    }

    const statusSelection =
      this.statusFilterValue ||
      String((filterModel?.['status'] as { filter?: unknown })?.filter ?? '').trim();
    const statusFilter = this.mapStatusSelectionToApi(statusSelection);
    if (statusFilter) {
      newFilters.push(statusFilter);
    }

    if (newFilters.length === 0) {
      newFilters.push({ field: '', operator: '', value: '' });
    }

    this.payloadOfgetCustomerList.filters = newFilters;
  }

  private resolveCustomerTypeFilterSelection(
    filterModel: Record<string, unknown> | null | undefined
  ): string | null {
    if (this.customerTypeFilterValue) {
      return this.customerTypeFilterValue;
    }
    const raw = (filterModel?.['customerTypeName'] as { filter?: unknown })?.filter;
    if (raw == null || String(raw).trim() === '') {
      return null;
    }
    return String(raw).trim();
  }

  private applyCustomerTypeClientFilter(
    rows: any[],
    selection: string | null
  ): any[] {
    if (!selection) {
      return rows;
    }
    return rows.filter((row) => this.formatCustomerType(row) === selection);
  }

  private mapAgGridFilterToApiFilter(
    field: string,
    filter: Record<string, unknown>
  ): CustomerListFilter | null {
    const raw = filter?.['filter'];
    if (raw == null || String(raw).trim() === '') {
      return null;
    }

    // Device Count uses `like` across User Management (same as Config List).
    if (field === 'deviceCount') {
      return {
        field,
        operator: 'like',
        value: String(raw).trim(),
      };
    }

    if (field === 'customerTypeName') {
      return this.mapCustomerTypeSelectionToApi(String(raw).trim());
    }

    return { field, operator: 'like', value: String(raw).trim() };
  }

  private mapCustomerTypeSelectionToApi(
    selection: string
  ): CustomerListFilter | null {
    if (!selection) {
      return null;
    }
    if (selection === 'Reseller') {
      return {
        field: 'customerTypeId',
        operator: 'equals',
        value: String(CUSTOMER_TYPE_ID_RESELLER),
      };
    }
    if (selection === 'Customer') {
      return {
        field: 'customerTypeId',
        operator: 'equals',
        value: String(CUSTOMER_TYPE_ID_CUSTOMER),
      };
    }
    return {
      field: 'customerTypeName',
      operator: 'like',
      value: selection,
    };
  }

  private mapCloudProviderSelectionToApi(
    selection: string
  ): CustomerListFilter | null {
    const value = selection?.trim();
    if (!value) {
      return null;
    }
    return {
      field: 'cloudProvider',
      operator: 'like',
      value,
    };
  }

  private mapStatusSelectionToApi(selection: string): CustomerListFilter | null {
    if (!selection) {
      return null;
    }
    if (selection === 'Active') {
      return { field: 'status', operator: 'eq', value: 'ACTIVE' };
    }
    if (selection === 'Inactive') {
      return { field: 'status', operator: 'eq', value: 'INACTIVE' };
    }
    return { field: 'status', operator: 'like', value: selection };
  }

  /** Map removed/hidden grid columns to API sort fields. */
  private resolveApiSortField(colId: string): string {
    if (colId === 'customerId' || colId === 'createdAt') {
      return 'createdAt';
    }
    if (
      colId === 'customerTypeName' ||
      colId === 'customerType' ||
      colId === 'customerTypeId'
    ) {
      return 'customerTypeId';
    }
    return colId;
  }

  /** Skip grid sort UI for columns that are not displayed. */
  private resolveGridSortColId(colId: string): string | null {
    if (colId === 'customerId' || colId === 'createdAt') {
      return null;
    }
    return colId;
  }

  /** Display Reseller or Customer from BE type fields; infer when omitted. */
  private formatCustomerType(data: any): string {
    if (!data) {
      return 'N/A';
    }

    const typeRef = data.customerType;
    if (typeRef && typeof typeRef === 'object') {
      const label =
        typeRef.label ?? typeRef.name ?? typeRef.typeName ?? typeRef.typeCode;
      if (label != null && String(label).trim() !== '') {
        return this.normalizeCustomerTypeLabel(String(label));
      }
    }

    if (typeof typeRef === 'string' && typeRef.trim()) {
      return this.normalizeCustomerTypeLabel(typeRef);
    }

    const labelCandidates = [
      data.customerTypeName,
      data.customerTypeLabel,
      data.typeName,
      data.typeCode,
    ];
    for (const candidate of labelCandidates) {
      if (candidate != null && String(candidate).trim() !== '') {
        return this.normalizeCustomerTypeLabel(String(candidate));
      }
    }

    const typeId = data.customerTypeId ?? data.typeId;
    if (typeId != null && typeId !== '') {
      const numericId = Number(typeId);
      if (numericId === CUSTOMER_TYPE_ID_RESELLER) {
        return 'Reseller';
      }
      if (numericId === CUSTOMER_TYPE_ID_CUSTOMER) {
        return 'Customer';
      }
    }

    const parentId =
      data.parentResellerId ??
      data.parentCustomerId ??
      data.parentId ??
      data.resellerId;
    if (parentId != null && parentId !== '' && Number(parentId) > 0) {
      return 'Customer';
    }

    return 'N/A';
  }

  private normalizeCustomerTypeLabel(value: string): string {
    const trimmed = value.trim();
    const upper = trimmed.toUpperCase();
    if (upper === 'RESELLER') {
      return 'Reseller';
    }
    if (upper === 'CUSTOMER') {
      return 'Customer';
    }
    return trimmed;
  }

  /** Display status from BE `status` string; fallback to legacy `isActive` boolean. */
  private formatCustomerStatus(data: any): string {
    if (!data) return 'N/A';

    const status = data.status;
    if (status != null && String(status).trim() !== '') {
      const normalized = String(status).trim().toUpperCase();
      if (normalized === 'ACTIVE') return 'Active';
      if (normalized === 'INACTIVE') return 'Inactive';
      return String(status).trim();
    }

    if (typeof data.isActive === 'boolean') {
      return data.isActive ? 'Active' : 'Inactive';
    }

    return 'N/A';
  }

  // Method to get customers for dropdown
  getAllCustomersForDropdown() {
    const dropdownPayload = {
      page: 0,
      size: 100,
      filters: [
        {
          field: '',
          operator: '',
          value: '',
        },
      ],
      sorts: [
        {
          field: 'customerName',
          direction: 'asc',
        },
      ],
    };

    this.customerService
      .getAllCustomer(dropdownPayload)
      .subscribe((res) => {
        this.customerList = res?.data?.content;
      });
  }

  // Filter change handlers
  onCustomerNameChange(event: any) {
    this.customerNameFilter = event.target.value;
    this.applyFilters();
  }

  // Method to apply toolbar filters (merged with column filters on reload)
  applyFilters() {
    this.clientFilteredRows = null;
    this.clientFilteredCacheKey = '';
    this.refreshCustomerGrid();
  }

  // Add export functionality
  isLoading = false;

  downloadCustomers() {
    this.isLoading = true;
    const body = {
      page: 0,
      size: this.total_number_of_records,
      filters: this.payloadOfgetCustomerList.filters,
      sorts: this.payloadOfgetCustomerList.sorts,
    };

    this.customerService.getAllCustomer(body)
      .subscribe({
        next: (data) => {
          const csvData = this.convertToCSV(data.data.content);
          this.downloadCSV(csvData, 'CustomerList.csv');
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
        }
      });
  }

  // Helper function to convert JSON data to CSV format
  convertToCSV(data: any[]): string {
    if (data.length === 0) return '';

    const replacer = (key: any, value: any) => value === null ? 'N/A' : value;
    const header = Object.keys(data[0]);
    const csv = [
      header.join(','),
      ...data.map(row => header.map(fieldName =>
        JSON.stringify(row[fieldName], replacer)).join(','))
    ];
    return csv.join('\r\n');
  }

  /** DELETE /customers/v1/customer/{id} requires the internal record id, not customerId. */
  private resolveCustomerIdFromRow(
    row: Record<string, unknown>
  ): number | string | null {
    const raw = row['id'];
    if (raw == null || raw === '') {
      return null;
    }
    const numeric = Number(raw);
    if (Number.isFinite(numeric) && numeric > 0) {
      return numeric;
    }
    if (typeof raw === 'string' && raw.trim()) {
      return raw.trim();
    }
    return null;
  }

  // Helper function to trigger CSV download
  downloadCSV(csvData: string, filename: string) {
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }
}
